/* =============================================================================
   VIEWS / ADMIN / TABLE-CART — Tisch-Warenkorb (S196 Cut b1)
   =============================================================================
   Slide-over (S187-Pattern, rechts angedockt) für die EINE offene, unbezahlte
   Order an einem Tisch/einer Belegung. Zeigt die Positionen nach Bestell-Runde
   (round_no) gruppiert und lässt sie BIS ZUR BEZAHLUNG bearbeiten (Jonas-Entscheid):
     · ± Menge          → orders.setItemQty (RPC set_order_item_qty)
     · Position entfernen → orders.removeItem (RPC remove_order_item, styled Confirm)
     · Kommentar/Position → orders.setItemNotes (RPC set_order_item_notes, Save-on-Blur)
     · ✏️ Position bearbeiten → Solo-Konfigurator (S197 Cut b2): orderItemToLine →
       vorbefüllter Anpassen-Konfigurator → lineToRpcItem → orders.updateItem
       (RPC update_order_item).
     · ✏️ Deal-Position bearbeiten → Solo-DEAL-Konfigurator (S198 Cut b3):
       deal_detail(id) laden → openDealSoloConfigurator(paar, detail) → beide Zeilen
       (Food + optional Drink) in EINEM Aufruf via orders.updateDealPosition
       (RPC update_deal_position). Der Deal wird als EINE Einheit behandelt:
       ✏️ hängt nur an der Food-Zeile, die Drink-Partner-Zeile trägt keine eigenen
       Controls; ± spiegelt die Menge aufs Paar, 🗑 entfernt das ganze Paar.
     · „+ Runde"         → bestehender Append-Pfad (openOrderForOccupancy)

   Nach paid/cancelled → read-only (Storno-/Korrektur-Pfad übernimmt).

   Realtime: die Mutations-RPCs fassen die orders-Zeile an (recompute_order_totals
   / updated_at) → order:realtime propagiert an KDS/Theke (kein order_items-Abo).
   Lokal rendert der Warenkorb nach jeder Mutation frisch (Cache invalidiert).

   Optik: normales Dark-Mode-Cockpit-Design (Standard-Tokens, hoher Kontrast).
   NICHT der Papier-Look der Speisekarte — der Warenkorb ist eine Operator-/
   Cockpit-Ansicht, kein Gast-Bestellblick (Jonas S196: „keine Ansicht für den
   Gast, hat nichts mit der Speisekarte zu tun"). Das ordering_ui_paper-Prinzip
   gilt für GAST-facing Bestell-Oberflächen + den geteilten Picker, nicht hier.
============================================================================ */

import { htmlEscape, fmtEUR, formatCustomizationsByCategory } from '../../utils/helpers.js';
import {
  fetchItemsFor, invalidateItems, groupItemsByRound,
  fetchRoundStatusFor, invalidateRoundStatus, roundStatusFor
} from '../../core/order-items-cache.js';
// S197 Cut b2 — Pure-Mapper für den „Position bearbeiten"-Re-Edit.
import { orderItemToLine, lineToRpcItem } from './admin-order-entry-state.js';

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { orders: [] };
}
function ordersAdapter() {
  return window.App && window.App.storage && window.App.storage.orders;
}

// -----------------------------------------------------------------------------
// Pure: darf diese Order noch bearbeitet werden? (bis Bezahlung; Storno raus)
// -----------------------------------------------------------------------------
export function canEditCart(order) {
  return !!order
    && order.paymentStatus !== 'paid'
    && order.status !== 'cancelled'
    && !order.isCorrection;
}

// -----------------------------------------------------------------------------
// S198 Cut b3 — Pure: Deal-Rollen der Positionen bestimmen. Ein Deal = 1–2 Zeilen,
// verklammert über customizations._dealMeta.line_uid (Food + optional Drink, S197-
// verifiziert). Der Warenkorb behandelt den Deal als EINE Einheit:
//   · Anchor  = Food-Zeile (die Nicht-Getränke-Zeile; sonst die erste) → trägt ✏️/±/🗑
//   · Partner = Drink-Zeile → keine eigenen Controls (Menge/Remove laufen übers Paar)
// Rückgabe: Map itemId → { isDealAnchor, isDealPartner, anchorId, partnerIds[],
//   memberIds[], dealId, lineUid }. Nicht-Deal-Zeilen fehlen in der Map (=> null).
// -----------------------------------------------------------------------------
const _DRINK_CATS = new Set(['getränke', 'getraenke', 'drinks', 'beverages']);
function _isDrinkCat(cat) { return _DRINK_CATS.has(String(cat || '').toLowerCase()); }

export function dealRolesFor(items) {
  const byUid = new Map();
  (Array.isArray(items) ? items : []).forEach(it => {
    const meta = it && it.customizations && it.customizations._dealMeta;
    if (!meta || !meta.line_uid) return;
    if (!byUid.has(meta.line_uid)) byUid.set(meta.line_uid, []);
    byUid.get(meta.line_uid).push(it);
  });
  const roles = new Map();
  byUid.forEach((group, uid) => {
    const anchor = group.find(g => !_isDrinkCat(g.categorySnapshot)) || group[0];
    const memberIds = group.map(g => g.id);
    const partnerIds = group.filter(g => g.id !== anchor.id).map(g => g.id);
    const dealId = (anchor.customizations && anchor.customizations._dealMeta || {}).id || null;
    group.forEach(g => {
      roles.set(g.id, {
        isDealAnchor: g.id === anchor.id,
        isDealPartner: g.id !== anchor.id,
        anchorId: anchor.id,
        partnerIds, memberIds, dealId, lineUid: uid
      });
    });
  });
  return roles;
}

// -----------------------------------------------------------------------------
// Slide-over-Gerüst (S187-Muster, paper-look)
// -----------------------------------------------------------------------------
let _cartOrderId = null;
let _cartKeyHandler = null;

function _ensureSlideover() {
  let ov = document.getElementById('tc-slideover');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'tc-slideover';
    ov.className = 'tc-slideover-overlay';
    ov.innerHTML = `<div class="tc-slideover-panel" role="dialog" aria-modal="true"><div id="tc-slideover-content"></div></div>`;
    document.body.appendChild(ov);
    ov.addEventListener('click', (e) => { if (e.target === ov) closeTableCart(); });
  }
  if (!_cartKeyHandler) {
    _cartKeyHandler = (e) => { if (e.key === 'Escape') closeTableCart(); };
    document.addEventListener('keydown', _cartKeyHandler, true);
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => ov.classList.add('open'));
  else ov.classList.add('open');
  return document.getElementById('tc-slideover-content');
}

export function closeTableCart() {
  const ov = document.getElementById('tc-slideover');
  if (ov && ov.parentNode) ov.parentNode.removeChild(ov);
  if (_cartKeyHandler) { document.removeEventListener('keydown', _cartKeyHandler, true); _cartKeyHandler = null; }
  _cartOrderId = null;
}

// -----------------------------------------------------------------------------
// Öffnen + Rendern
// -----------------------------------------------------------------------------
export async function openTableCart(orderId) {
  // Permission-Gate wie „Bestellung aufnehmen" (order.create deckt das Anpassen ab).
  if (window.App && window.App.auth && typeof window.App.auth.can === 'function'
      && !window.App.auth.can('order.create')) {
    if (typeof window.showToast === 'function') window.showToast('Für diese Aktion fehlt dir die Berechtigung.', 'error');
    return;
  }
  if (!orderId) return;
  _ensureRealtimeWired();
  _cartOrderId = orderId;
  const content = _ensureSlideover();
  if (!content) return;
  content.innerHTML = `<div class="tc-head"><h2>Warenkorb</h2><button class="tc-close" onclick="closeTableCart()">×</button></div><p class="tc-hint" style="padding:16px;">Lade…</p>`;
  await renderTableCart();
}

/** Frisch rendern (nach jeder Mutation + bei Realtime). */
export async function renderTableCart() {
  const content = document.getElementById('tc-slideover-content');
  if (!content || !_cartOrderId) return;
  const order = (getState().orders || []).find(o => o.id === _cartOrderId);
  if (!order) {
    content.innerHTML = `<div class="tc-head"><h2>Warenkorb</h2><button class="tc-close" onclick="closeTableCart()">×</button></div><p class="tc-hint" style="padding:16px;">Order nicht mehr verfügbar.</p>`;
    return;
  }
  // Positionen + Runden-Status laden (Cache-first).
  const [items] = await Promise.all([
    fetchItemsFor(_cartOrderId),
    fetchRoundStatusFor(_cartOrderId)
  ]);
  if (_cartOrderId !== order.id) return; // zwischenzeitlich geschlossen/gewechselt
  const editable = canEditCart(order);
  const rounds = groupItemsByRound(items);
  const rcs = roundStatusFor(_cartOrderId);
  const roles = dealRolesFor(items); // S198 Cut b3 — Deal-Paar-Rollen (Anchor/Partner)
  const totalCents = Number(order.totalGrossCents != null ? order.totalGrossCents : 0);

  const title = order.orderNumber ? `Warenkorb · ${htmlEscape(order.orderNumber)}` : 'Warenkorb';
  const roLine = editable ? '' :
    `<div class="tc-ro-banner">${order.paymentStatus === 'paid' ? '✓ Bezahlt' : (order.status === 'cancelled' ? 'Storniert' : 'Nur Ansicht')} — Positionen sind nur noch einsehbar.</div>`;

  const roundsHtml = rounds.length
    ? rounds.map(g => renderRound(g, rcs, editable, roles)).join('')
    : `<p class="tc-hint" style="padding:8px 2px;">Noch keine Positionen.</p>`;

  content.innerHTML = `
    <div class="tc-head">
      <h2>${title}</h2>
      <button class="tc-close" onclick="closeTableCart()" aria-label="Schließen">×</button>
    </div>
    <div class="tc-body">
      ${roLine}
      <div id="tc-guest-draft"></div>
      ${roundsHtml}
    </div>
    <div class="tc-footer">
      <div class="tc-total">Summe <strong>${fmtEUR(totalCents / 100)}</strong></div>
      <div class="tc-footer-actions">
        ${editable ? `<button class="btn btn-primary btn-sm" onclick="tcAddRound()">+ Runde hinzufügen</button>` : ''}
        <button class="btn btn-ghost btn-sm" onclick="closeTableCart()">Schließen</button>
      </div>
    </div>
  `;

  // S244 Cut B — Gast-Entwurf nachladen und OBEN einblenden. Bewusst read-only:
  // was der Gast noch nicht abgeschickt hat, gehoert ihm. Der Service soll es
  // sehen (damit er beim Kassieren nicht ueberrascht wird), aber nicht
  // heimlich mitbestellen.
  _renderGuestDraft(order);
}

async function _renderGuestDraft(order) {
  const occId = order && (order.occupancyId || order.occupancy_id);
  const host = document.getElementById('tc-guest-draft');
  if (!host || !occId) return;
  const adapter = window.App && window.App.storage && window.App.storage.tableOccupancies;
  if (!adapter || typeof adapter.guestCartForOccupancy !== 'function') return;
  let rows = [];
  try { rows = await adapter.guestCartForOccupancy(occId); }
  catch (_) { return; }
  // Modal koennte inzwischen geschlossen/gewechselt sein.
  const live = document.getElementById('tc-guest-draft');
  if (!live || !rows.length) { if (live) live.innerHTML = ''; return; }
  const sum = rows.reduce((s, r) => s + (Number(r.line_total_cents) || 0), 0);
  live.innerHTML = `
    <div class="tc-guest-draft">
      <div class="tc-guest-draft-head">
        📱 Gast-Entwurf — <strong>noch nicht abgesendet</strong>
        (${rows.length} ${rows.length === 1 ? 'Position' : 'Positionen'} · ${fmtEUR(sum / 100)})
      </div>
      ${rows.map(r => `
        <div class="tc-guest-draft-line">
          <span>${r.qty}× ${htmlEscape((r.item && r.item.name) || 'Position')}${r.guest_name ? ` <em>(${htmlEscape(r.guest_name)})</em>` : ''}</span>
          <span>${fmtEUR((Number(r.line_total_cents) || 0) / 100)}</span>
        </div>`).join('')}
      <div class="tc-guest-draft-hint">Die Gäste tippen gerade noch. Erscheint als eigene Runde, sobald sie absenden.</div>
    </div>`;
}

function channelBadge(rcs, round, channel, label) {
  const row = rcs.get(round + '|' + channel);
  if (!row) return '';
  const st = row.status || 'pending';
  const map = { pending: 'offen', preparing: 'in Arbeit', ready: 'fertig' };
  return `<span class="tc-round-chan tc-chan-${st}">${label}: ${map[st] || st}</span>`;
}

function renderRound(group, rcs, editable, roles) {
  const round = group.round;
  const kit = channelBadge(rcs, round, 'kitchen', '🍔 Küche');
  const bar = channelBadge(rcs, round, 'bar', '🥤 Bar');
  // „in Zubereitung"-Hinweis (kein Hard-Block): Position bleibt editierbar bis Bezahlung.
  const inPrep = ['kitchen', 'bar'].some(ch => {
    const r = rcs.get(round + '|' + ch);
    return r && (r.status === 'preparing' || r.status === 'ready');
  });
  const prepHint = (editable && inPrep)
    ? `<div class="tc-round-hint">⚠️ Runde ist schon in Zubereitung — Änderungen bitte der Küche/Theke sagen.</div>`
    : '';
  return `
    <div class="tc-round">
      <div class="tc-round-head">
        <span class="tc-round-no">Runde ${round}</span>
        <span class="tc-round-chans">${kit}${bar}</span>
      </div>
      ${prepHint}
      ${group.items.map(it => renderCartItem(it, editable, (roles && roles.get(it.id)) || null)).join('')}
    </div>
  `;
}

function renderCartItem(it, editable, role) {
  const custLines = formatCustomizationsByCategory(it.customizations, it.categorySnapshot) || [];
  const custHtml = custLines.length
    ? `<div class="tc-item-cust">${custLines.map(l => htmlEscape(l)).join(' · ')}</div>`
    : '';
  const qty = Number(it.qty || 1);
  const lineTotal = fmtEUR(Number(it.lineTotalCents || 0) / 100);
  const id = htmlEscape(it.id);

  const cust = (it.customizations && typeof it.customizations === 'object') ? it.customizations : {};
  const isDeal = !!cust._dealMeta;
  const isDealAnchor  = !!(role && role.isDealAnchor);
  const isDealPartner = !!(role && role.isDealPartner);
  const menuList = getState().menu || [];
  const menuExists = !!menuList.find(m => m.id === it.menuItemId);

  // S198 Cut b3 — Deal-Partner (Drink-Zeile): keine eigenen Controls. Menge folgt
  // dem Anchor (± spiegelt), Entfernen/Bearbeiten laufen über die Food-Zeile.
  if (isDealPartner) {
    const notesRo = (it.notes && String(it.notes).trim())
      ? `<div class="tc-item-note-ro">📝 ${htmlEscape(String(it.notes).trim())}</div>` : '';
    return `
      <div class="tc-item tc-item-dealpartner" data-item-id="${id}">
        <div class="tc-item-top">
          <div class="tc-qty"><span class="tc-qty-val">${qty}×</span></div>
          <div class="tc-item-main">
            <div class="tc-item-name">${htmlEscape(it.nameSnapshot || '(unbenannt)')}</div>
            ${custHtml}
            <div class="tc-item-dealhint">🎁 Teil des Aktions-Menüs</div>
          </div>
          <div class="tc-item-right"><span class="tc-item-price">${lineTotal}</span></div>
        </div>
        ${notesRo}
      </div>
    `;
  }

  const qtyCtrl = editable
    ? `<div class="tc-qty">
         <button class="tc-qty-btn" onclick="tcSetQty('${id}', ${qty - 1})" ${qty <= 1 ? 'disabled title="Zum Entfernen den Papierkorb nutzen"' : ''}>−</button>
         <span class="tc-qty-val">${qty}</span>
         <button class="tc-qty-btn" onclick="tcSetQty('${id}', ${qty + 1})">+</button>
       </div>`
    : `<div class="tc-qty"><span class="tc-qty-val">${qty}×</span></div>`;

  const removeBtn = editable
    ? `<button class="tc-item-remove" onclick="tcRemoveItem('${id}', '${htmlEscape(String(it.nameSnapshot || '').replace(/'/g, ''))}')" title="${isDealAnchor ? 'Aktions-Menü entfernen' : 'Position entfernen'}">🗑</button>`
    : '';

  // ✏️ Bearbeiten:
  //  · Deal-Anchor (S198 Cut b3) → tcEditDeal (öffnet den Deal-Konfigurator, editiert
  //    das ganze Paar). Immer angeboten; ist der Deal nicht mehr auflösbar, klärt der
  //    Klick-Pfad das mit einem Hinweis.
  //  · Normale Position (S197 Cut b2) → tcEditItem, nur wenn Menü-Gericht noch existiert.
  let editBtn = '';
  if (editable && isDealAnchor) {
    editBtn = `<button class="tc-item-edit" onclick="tcEditDeal('${id}')" title="Aktions-Menü bearbeiten (Gericht-Optionen, Beilage, Getränk)">✏️</button>`;
  } else if (editable && !isDeal && menuExists) {
    editBtn = `<button class="tc-item-edit" onclick="tcEditItem('${id}')" title="Position bearbeiten (Brötchen/Extras/ohne Zutat/Notiz)">✏️</button>`;
  }
  const dealHint = isDealAnchor ? `<div class="tc-item-dealhint">🎁 Aktions-Menü</div>` : '';

  // S197 Cut b2 — Kommentar-Inline-Edit kehrt zurück (bis Bezahlung editierbar,
  // Save-on-Blur via onchange → set_order_item_notes). Read-only nach paid/cancelled.
  const notesField = editable
    ? `<textarea class="tc-item-notes" rows="1" placeholder="Kommentar für Küche/Theke…"
         onchange="tcSaveNotes('${id}', this.value)">${htmlEscape(it.notes || '')}</textarea>`
    : (it.notes && String(it.notes).trim()
        ? `<div class="tc-item-note-ro">📝 ${htmlEscape(String(it.notes).trim())}</div>` : '');

  return `
    <div class="tc-item${isDealAnchor ? ' tc-item-dealanchor' : ''}" data-item-id="${id}">
      <div class="tc-item-top">
        ${qtyCtrl}
        <div class="tc-item-main">
          <div class="tc-item-name">${htmlEscape(it.nameSnapshot || '(unbenannt)')}</div>
          ${custHtml}
          ${dealHint}
        </div>
        <div class="tc-item-right">
          <span class="tc-item-price">${lineTotal}</span>
          ${editBtn}
          ${removeBtn}
        </div>
      </div>
      ${notesField}
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Mutations-Handler (window-gebunden für inline-onclick)
// -----------------------------------------------------------------------------
async function _afterMutation() {
  invalidateItems(_cartOrderId);
  invalidateRoundStatus(_cartOrderId);
  await renderTableCart();
}

export async function tcSetQty(itemId, qty) {
  const n = Number(qty);
  if (!Number.isFinite(n) || n < 1) return; // −  bei 1 ist deaktiviert; entfernen ist explizit
  const s = ordersAdapter();
  if (!s || typeof s.setItemQty !== 'function') return;
  try {
    // S198 Cut b3 — Deal-Paar: Menge auf die Partner-Zeile(n) spiegeln (gleicher
    // line_uid), sonst desyncen Food-/Drink-Menge. Anchor zuerst, dann Partner.
    const items = await fetchItemsFor(_cartOrderId);
    const it = (items || []).find(x => x.id === itemId);
    const meta = it && it.customizations && it.customizations._dealMeta;
    await s.setItemQty(itemId, n);
    if (meta && meta.line_uid) {
      const partners = (items || []).filter(x => x.id !== itemId
        && x.customizations && x.customizations._dealMeta
        && x.customizations._dealMeta.line_uid === meta.line_uid);
      for (const p of partners) { await s.setItemQty(p.id, n); }
    }
    await _afterMutation();
  } catch (err) {
    console.error('[tcSetQty]', err);
    if (!(err && err.operatorRequired) && typeof window.showToast === 'function') {
      window.showToast('Menge ändern fehlgeschlagen: ' + (err.message || err), 'error');
    }
    await renderTableCart();
  }
}

export async function tcRemoveItem(itemId, name) {
  const s = ordersAdapter();
  if (!s || typeof s.removeItem !== 'function') return;

  // S198 Cut b3 — Deal-Zeile? Dann das GANZE Paar (gleicher line_uid) entfernen,
  // damit keine Waisen-Zeile mit dangling _dealMeta zurückbleibt (Reporting/Rabatt).
  const items = await fetchItemsFor(_cartOrderId);
  const it = (items || []).find(x => x.id === itemId);
  const meta = it && it.customizations && it.customizations._dealMeta;
  const pairIds = (meta && meta.line_uid)
    ? (items || []).filter(x => x.customizations && x.customizations._dealMeta
        && x.customizations._dealMeta.line_uid === meta.line_uid).map(x => x.id)
    : [itemId];
  const isDeal = pairIds.length > 1 || !!meta;

  // Vorab-Check: würde das die gesamte Order leeren? → stornieren statt leeren
  // (remove_order_item blockt die letzte Position serverseitig; hier sauber vorweg).
  if ((items || []).length > 0 && pairIds.length >= (items || []).length) {
    if (typeof window.showToast === 'function') {
      window.showToast('Das ist die einzige Position — bitte die Bestellung stornieren.', 'info');
    }
    return;
  }

  const res = await window.styledConfirm({
    title: isDeal ? 'Aktions-Menü entfernen?' : 'Position entfernen?',
    body: isDeal
      ? (name ? `Das Aktions-Menü „${name}" (inkl. Getränk) von dieser Bestellung entfernen?` : 'Dieses Aktions-Menü von der Bestellung entfernen?')
      : (name ? `„${name}" von dieser Bestellung entfernen?` : 'Diese Position von der Bestellung entfernen?'),
    danger: true, confirmLabel: 'Entfernen'
  });
  if (!res.confirmed) return;
  try {
    // Partner zuerst, Anchor zuletzt (hält den „letzte Position"-Guard scharf).
    for (const pid of pairIds.filter(x => x !== itemId)) { await s.removeItem(pid); }
    await s.removeItem(itemId);
    await _afterMutation();
    if (typeof window.showToast === 'function') {
      window.showToast(isDeal ? 'Aktions-Menü entfernt.' : 'Position entfernt.', 'success');
    }
  } catch (err) {
    console.error('[tcRemoveItem]', err);
    if (!(err && err.operatorRequired) && typeof window.showToast === 'function') {
      window.showToast('Entfernen fehlgeschlagen: ' + (err.message || err), 'error');
    }
    await renderTableCart();
  }
}

/** S197 Cut b2 — Positions-Kommentar inline im Warenkorb speichern (bis Bezahlung,
 *  Save-on-Blur via onchange). onchange feuert erst beim Verlassen des Feldes → kein
 *  Trailing-Race (Memory feedback_gg_debounce_trailing_race). Kein voller Re-Render
 *  (Fokus/Scroll bleiben) — nur Cache invalidieren, damit KDS/Theke frisch ziehen. */
export async function tcSaveNotes(itemId, value) {
  const s = ordersAdapter();
  if (!s || typeof s.setItemNotes !== 'function') return;
  try {
    await s.setItemNotes(itemId, value);
    invalidateItems(_cartOrderId);
    if (typeof window.showToast === 'function') window.showToast('Kommentar gespeichert.', 'success');
  } catch (err) {
    console.error('[tcSaveNotes]', err);
    if (!(err && err.operatorRequired) && typeof window.showToast === 'function') {
      window.showToast('Kommentar speichern fehlgeschlagen: ' + (err.message || err), 'error');
    }
  }
}

/** S197 Cut b2 — „Position bearbeiten": gespeicherte Position → Konfigurator-Line
 *  (orderItemToLine) → Solo-Konfigurator vorbefüllt öffnen. Speichern mappt zurück
 *  (lineToRpcItem) → update_order_item. Deal/Menü-weg → nicht editierbar (Hinweis). */
export async function tcEditItem(itemId) {
  const s = ordersAdapter();
  if (!s || typeof s.updateItem !== 'function') return;
  if (typeof window.openSoloConfigurator !== 'function') {
    if (typeof window.showToast === 'function') window.showToast('Konfigurator nicht verfügbar.', 'error');
    return;
  }
  const items = await fetchItemsFor(_cartOrderId);
  const it = (items || []).find(x => x.id === itemId);
  if (!it) return;
  const st = getState();
  const menuList = st.menu || [];
  const line = orderItemToLine(it, menuList);
  if (!line) {
    if (typeof window.showToast === 'function') {
      window.showToast('Diese Position ist nicht einzeln bearbeitbar (Deal-Position oder Gericht nicht mehr im Menü).', 'info');
    }
    return;
  }
  const settings = st.settings || {};
  window.openSoloConfigurator(line, {
    itemId,
    onConfirm: async (draft) => {
      try {
        const rpcItem = lineToRpcItem(draft, menuList, settings);
        await s.updateItem(itemId, {
          customizations: rpcItem.customizations,
          unitPriceCents: rpcItem.unitPriceCents,
          notes:          rpcItem.notes
        });
        await _afterMutation();
        if (typeof window.showToast === 'function') window.showToast('Position aktualisiert.', 'success');
      } catch (err) {
        console.error('[tcEditItem]', err);
        if (!(err && err.operatorRequired) && typeof window.showToast === 'function') {
          window.showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        }
        await renderTableCart();
      }
    },
    onClose: () => { renderTableCart(); }
  });
}

/** S198 Cut b3 — deal_detail(id) frisch laden (Operator-Pfad, Fenster egal). Der
 *  RPC prüft nur den Tenant-Besitz, kein Zeitfenster → funktioniert auch für einen
 *  Deal ausserhalb seiner Aktionszeit. Fehler/gelöscht → null (Aufrufer: Hinweis). */
async function _loadDealDetail(dealId) {
  const supa = window.App && window.App.supabase;
  if (!supa || !dealId || typeof supa.rpc !== 'function') return null;
  try {
    const { data, error } = await supa.rpc('deal_detail', { p_deal_id: dealId });
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.warn('[tcEditDeal] deal_detail:', e && e.message);
    return null;
  }
}

/** S198 Cut b3 — „Deal-Position bearbeiten": das persistierte Paar (Food + optional
 *  Drink, gleicher line_uid) → Solo-Deal-Konfigurator vorbefüllt öffnen. Speichern
 *  ruft update_deal_position über beide Zeilen in EINER Transaktion. Ist der Deal
 *  nicht mehr auflösbar (gelöscht / Gruppe/Gericht raus) → Hinweis, kein Re-Edit. */
export async function tcEditDeal(anchorId) {
  const s = ordersAdapter();
  if (!s || typeof s.updateDealPosition !== 'function') return;
  if (typeof window.openDealSoloConfigurator !== 'function') {
    if (typeof window.showToast === 'function') window.showToast('Deal-Konfigurator nicht verfügbar.', 'error');
    return;
  }
  const items = await fetchItemsFor(_cartOrderId);
  const anchor = (items || []).find(x => x.id === anchorId);
  const meta = anchor && anchor.customizations && anchor.customizations._dealMeta;
  if (!meta || !meta.line_uid) return;
  const rows = (items || []).filter(x => x.customizations && x.customizations._dealMeta
    && x.customizations._dealMeta.line_uid === meta.line_uid);

  const detail = await _loadDealDetail(meta.id);
  if (!detail) {
    if (typeof window.showToast === 'function') {
      window.showToast('Diese Aktion ist nicht mehr bearbeitbar (Deal gelöscht) — bitte entfernen und neu aufnehmen.', 'info');
    }
    return;
  }
  const opened = window.openDealSoloConfigurator(rows, detail, {
    lineUid: meta.line_uid,
    onConfirm: async ({ drafts, lineUid }) => {
      try {
        await s.updateDealPosition(_cartOrderId, lineUid, drafts);
        await _afterMutation();
        if (typeof window.showToast === 'function') window.showToast('Aktions-Menü aktualisiert.', 'success');
      } catch (err) {
        console.error('[tcEditDeal]', err);
        if (!(err && err.operatorRequired) && typeof window.showToast === 'function') {
          window.showToast('Speichern fehlgeschlagen: ' + (err.message || err), 'error');
        }
        await renderTableCart();
      }
    },
    onClose: () => { renderTableCart(); }
  });
  if (!opened && typeof window.showToast === 'function') {
    window.showToast('Diese Aktion ist nicht mehr bearbeitbar (Gruppe/Gericht geändert) — bitte entfernen und neu aufnehmen.', 'info');
  }
}

/** „+ Runde" — bestehenden Append-Pfad wiederverwenden. Belegung → Order-Aufnahme
 *  (setzt _pendingOccupancyIdForOrder, Submit hängt via _findOpenOrderForContainer
 *  an DIESE Order an). Reservierungs-Order → Reservierungs-Detail (dortiger Add-Pfad). */
export function tcAddRound() {
  const order = (getState().orders || []).find(o => o.id === _cartOrderId);
  if (!order) return;
  closeTableCart();
  if (order.occupancyId && typeof window.openOrderForOccupancy === 'function') {
    window.openOrderForOccupancy(order.occupancyId);
  } else if (order.reservationId && typeof window.openReservationDetail === 'function') {
    window.openReservationDetail(order.reservationId);
  } else if (typeof window.showToast === 'function') {
    window.showToast('Nachbestellen für diese Order nicht verfügbar.', 'error');
  }
}

// -----------------------------------------------------------------------------
// Realtime: Order der offenen Warenkorb-Ansicht änderte sich → frisch rendern.
// Lazy beim ersten Öffnen verdrahtet (App.events existiert dann sicher) — kein
// top-level Polling (Memory gg-microtask-render-loop / test-sicher).
// -----------------------------------------------------------------------------
let _tcRealtimeWired = false;
function _ensureRealtimeWired() {
  if (_tcRealtimeWired || !(window.App && window.App.events)) return;
  _tcRealtimeWired = true;
  const onOrderChange = (payload) => {
    if (!_cartOrderId || !document.getElementById('tc-slideover')) return;
    const changedId = (payload && payload.new && payload.new.id)
      || (payload && payload.order && payload.order.id)
      || (payload && payload.id);
    // Ohne eindeutige ID (oder ID == offene Order) sicherheitshalber neu rendern.
    if (!changedId || String(changedId) === String(_cartOrderId)) {
      invalidateItems(_cartOrderId);
      invalidateRoundStatus(_cartOrderId);
      renderTableCart();
    }
  };
  window.App.events.on('order:realtime', onOrderChange);
  window.App.events.on('order:round_status:realtime', onOrderChange);
  window.App.events.on('order:items_appended', onOrderChange);
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openTableCart, closeTableCart, renderTableCart,
    tcSetQty, tcRemoveItem, tcSaveNotes, tcEditItem, tcEditDeal, tcAddRound
  });
}
